import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import axios from "axios";
import { API } from "../context/AppContext";
import GeoArt from "../components/GeoArt";
import ImageOrArt from "../components/ImageOrArt";
import Marquee from "../components/Marquee";
import { MaskedLines, Reveal, SectionHead } from "../components/Reveal";

const PILLARS = [
  { id: "field-faces", no: "01", title: "Field Faces", desc: "Exploring identity through bold visual storytelling.", to: "/field-faces", seed: "pillar-ff" },
  { id: "rise-show", no: "02", title: "RISE Art Show", desc: "An annual fundraising art exhibition supporting undergraduate students during Tuskegee University Homecoming.", to: "/rise", seed: "pillar-rise" },
  { id: "the-collection", no: "03", title: "The Collection", desc: "Building community through exhibitions, artist development, and cultural experiences.", to: "/the-collection", seed: "pillar-coll" },
  { id: "datos-pass", no: "04", title: "DATOS Gallery Pass", desc: "Technology designed for artists, collectors, curators, and galleries.", to: "/datos", seed: "pillar-datos" },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yArt = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    axios.get(`${API}/artworks`).then(({ data }) => setFeatured(data.filter((a) => a.featured))).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page">
      <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden grain pt-24" data-testid="hero-section">
        <div className="absolute inset-0 grid grid-cols-2 md:grid-cols-4 opacity-40">
          {["hero-a", "hero-b", "hero-c", "hero-d"].map((s, i) => (
            <motion.div key={s} style={{ y: yArt }} className={i % 2 ? "mt-16" : ""}>
              <GeoArt seed={s} className="w-full h-full" />
            </motion.div>
          ))}
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-transparent" />
        <motion.div style={{ y: yText }} className="relative container-x pb-16 sm:pb-24 w-full">
          <p className="eyebrow mb-6" data-testid="hero-eyebrow">Atuarra McCaslin — Artist · Curator · Founder</p>
          <MaskedLines
            lines={["Creating Culture", "Through Art,", "Community, and", "Technology"]}
            className="h-display max-w-5xl"
            data-testid="hero-headline"
          />
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-8 max-w-xl text-base text-muted-foreground"
            data-testid="hero-subheadline"
          >
            Atuarra McCaslin is a contemporary artist, curator, and founder whose work spans fine art, exhibitions, cultural programming, and digital tools for the creative ecosystem.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link to="/field-faces" className="btn-red" data-testid="hero-explore-field-faces">Explore Field Faces <ArrowRight size={16} /></Link>
            <Link to="/artwork" className="btn-ghost" data-testid="hero-view-artwork">View Artwork</Link>
          </motion.div>
        </motion.div>
      </section>

      <Marquee items={["SIDE QUESTS — Solo Exhibition 2025", "RISE — Tuskegee Homecoming", "Field Faces", "The Collection", "DATOS Gallery Pass", "Peace Love Power", "Art For Impact"]} />

      <section className="section-y" data-testid="four-pillars">
        <div className="container-x">
          <SectionHead eyebrow="The Ecosystem" title="One Artist. Four Pillars. A Cultural Platform." />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border border border-border">
            {PILLARS.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.08}>
                <Link
                  to={p.to}
                  data-testid={`pillar-card-${p.id}`}
                  className="group relative block bg-card p-8 sm:p-10 h-full min-h-[320px] flex flex-col justify-between overflow-hidden"
                >
                  <div className="absolute -right-10 -bottom-10 w-48 h-60 opacity-10 group-hover:opacity-30 transition-opacity duration-500 rotate-6">
                    <GeoArt seed={p.seed} className="w-full h-full" />
                  </div>
                  <div className="flex items-start justify-between">
                    <span className="font-mono-x text-xs text-[#FF1A1A]">{p.no}</span>
                    <ArrowUpRight size={20} className="text-muted-foreground group-hover:text-[#FF1A1A] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
                  </div>
                  <div className="relative">
                    <h3 className="font-display font-black uppercase text-2xl sm:text-3xl mb-3 group-hover:text-[#FF1A1A] transition-colors duration-300">{p.title}</h3>
                    <p className="text-sm text-muted-foreground max-w-xs">{p.desc}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-y border-t border-border" data-testid="featured-artworks">
        <div className="container-x">
          <SectionHead eyebrow="Selected Works" title="From the Field Faces Series" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {featured.slice(0, 3).map((a, i) => (
              <Reveal key={a.id} delay={i * 0.1}>
                <Link to={`/artwork/${a.id}`} className="group block" data-testid={`featured-artwork-${a.id}`}>
                  <div className="overflow-hidden border border-border">
                    <div className="transition-transform duration-700 group-hover:scale-105">
                      <ImageOrArt image={a.image} seed={a.seed} className="w-full" />
                    </div>
                  </div>
                  <div className="flex justify-between items-start mt-4">
                    <div>
                      <h3 className="font-display font-bold uppercase text-sm">{a.title}</h3>
                      <p className="font-mono-x text-xs text-muted-foreground mt-1">{a.year} — {a.medium}</p>
                    </div>
                    <span className="font-mono-x text-xs text-[#FF1A1A]">${a.price.toLocaleString()}</span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12 text-center">
            <Link to="/artwork" className="btn-ghost" data-testid="view-all-artwork">View Full Gallery <ArrowRight size={16} /></Link>
          </Reveal>
        </div>
      </section>

      <section className="section-y border-t border-border relative overflow-hidden" data-testid="manifesto-section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="eyebrow mb-4">The Practice</p>
              <h2 className="h-section mb-8">Bold Visual Narratives from the Black Experience in America</h2>
              <p className="text-muted-foreground max-w-xl mb-6">
                Field Faces is a growing cast of characters representing identity, memory, resilience, joy, and transformation — drawn from street art, cartoon aesthetics, neo-expressionism, and community storytelling.
              </p>
              <p className="text-muted-foreground max-w-xl mb-10">
                But the practice doesn't stop at the canvas. Through RISE, The Collection, and DATOS Gallery Pass, Atuarra is building the infrastructure — exhibitions, programs, and technology — for a whole creative ecosystem.
              </p>
              <Link to="/about" className="btn-red" data-testid="manifesto-about-cta">The Full Story <ArrowRight size={16} /></Link>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="border border-border p-4 rotate-2 hover:rotate-0 transition-transform duration-700">
                <GeoArt seed="manifesto" className="w-full" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
